import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, MapPin, Pencil, Plus, Star, Trash2, X } from "lucide-react";
import {
  MAX_SAVED_ADDRESSES,
  MX_STATES,
  addressInputSchema,
  type AddressInput,
  type SavedAddress,
} from "@nh/shared";
import { Alert, Badge, Button, Input } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import {
  useAddAddress,
  useMakeDefaultAddress,
  useMyAddresses,
  useRemoveAddress,
  useUpdateAddress,
} from "./address.api";
import { PlaceSearch } from "./PlaceSearch";

const EMPTY: AddressInput = { street: "", city: "", state: "", zip: "" };

/**
 * ═══════════════════════════════════════════════════════════════════
 * "Mis direcciones" (Anahí, 28-ago).
 *
 * ── UN campo, no cinco ───────────────────────────────────────────────
 * Se escribe la dirección en un solo buscador y Google devuelve calle,
 * colonia, ciudad, estado y CP de golpe. Los cinco campos siguen
 * existiendo —la guía de Trasso los necesita por separado— pero se
 * pintan ya rellenos, como confirmación, no como formulario. Quien
 * quiera escribirlos a mano abre "Escribir a mano"; y si el servidor no
 * tiene clave de Google, el formulario se abre solo.
 *
 * ── VARIAS direcciones, una predeterminada ───────────────────────────
 * La predeterminada es la que el checkout autollena. El servidor
 * garantiza que siempre haya exactamente una.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AddressSection() {
  const { t } = useTranslation();
  const addresses = useMyAddresses();
  const remove = useRemoveAddress();
  const makeDefault = useMakeDefaultAddress();
  const [editing, setEditing] = useState<SavedAddress | "new" | null>(null);

  const items = addresses.data ?? [];
  const full = items.length >= MAX_SAVED_ADDRESSES;

  return (
    <section className="grid gap-4 rounded-3xl border border-ds-border bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-ds-violet-pop" aria-hidden="true" />
          <h3 className="text-[15px] font-semibold text-ds-ink">{t("Mis direcciones de envío")}</h3>
        </div>
        {editing === null && !full && (
          <Button size="sm" variant="secondary" leftIcon={<Plus />} onClick={() => setEditing("new")}>
            {items.length === 0 ? t("Añadir mi dirección") : t("Añadir otra")}
          </Button>
        )}
      </div>

      {items.length > 0 && (
        <ul className="grid gap-2">
          {items.map((address) => (
            <li
              key={address.id}
              className={cn(
                "flex flex-wrap items-start gap-3 rounded-2xl border px-4 py-3",
                address.isDefault ? "border-ds-violet-pop bg-ds-lavender-bg/40" : "border-ds-border",
              )}
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  {address.label && (
                    <span className="text-[13px] font-semibold text-ds-ink">{address.label}</span>
                  )}
                  {address.isDefault && (
                    <Badge variant="indigo" size="sm">
                      {t("Predeterminada")}
                    </Badge>
                  )}
                </div>
                <p className="text-[13.5px] text-ds-ink">{address.street}</p>
                <p className="text-[12.5px] text-ds-ink-3">
                  {address.city}, {address.state} · CP {address.zip}
                  {address.reference ? ` · ${address.reference}` : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!address.isDefault && (
                  <button
                    type="button"
                    title={t("Usar por defecto")}
                    aria-label={t("Usar {{dir}} por defecto", { dir: address.street })}
                    onClick={() => makeDefault.mutate(address.id)}
                    className="rounded-full p-2 text-ds-ink-3 transition hover:bg-ds-lavender-bg hover:text-ds-violet-pop"
                  >
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <button
                  type="button"
                  title={t("Editar")}
                  aria-label={t("Editar {{dir}}", { dir: address.street })}
                  onClick={() => setEditing(address)}
                  className="rounded-full p-2 text-ds-ink-3 transition hover:bg-ds-lavender-bg hover:text-ds-violet-pop"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  title={t("Borrar")}
                  aria-label={t("Borrar {{dir}}", { dir: address.street })}
                  onClick={() => remove.mutate(address.id)}
                  className="rounded-full p-2 text-ds-ink-3 transition hover:bg-red-50 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {items.length === 0 && editing === null && (
        <p className="text-[13px] leading-relaxed text-ds-ink-3">
          {t("Guarda tu dirección y tus compras la traen ya escrita.")}
        </p>
      )}

      {editing !== null && (
        <AddressForm
          initial={editing === "new" ? null : editing}
          onDone={() => setEditing(null)}
        />
      )}

      {full && editing === null && (
        <p className="text-[12px] text-ds-ink-3">
          {t("Llegaste al máximo de {{n}} direcciones. Borra una para añadir otra.", {
            n: MAX_SAVED_ADDRESSES,
          })}
        </p>
      )}
    </section>
  );
}

/** El alta/edición: el buscador manda, los campos confirman. */
function AddressForm({
  initial,
  onDone,
}: {
  initial: SavedAddress | null;
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const add = useAddAddress();
  const update = useUpdateAddress();
  const [form, setForm] = useState<AddressInput>(() =>
    initial
      ? {
          street: initial.street,
          city: initial.city,
          state: initial.state,
          zip: initial.zip,
          ...(initial.reference ? { reference: initial.reference } : {}),
          ...(initial.label ? { label: initial.label } : {}),
          ...(initial.placeId ? { placeId: initial.placeId } : {}),
        }
      : { ...EMPTY },
  );
  // Los campos sueltos aparecen cuando ya hay algo que confirmar, cuando
  // se edita, o cuando quien escribe pide escribirlos a mano.
  const [manual, setManual] = useState(Boolean(initial));
  const [error, setError] = useState<string | null>(null);
  const saving = add.isPending || update.isPending;

  function set(patch: Partial<AddressInput>) {
    setForm((current) => ({ ...current, ...patch }));
  }

  function guardar() {
    setError(null);
    const parsed = addressInputSchema.safeParse(form);
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? t("Revisa los datos"));
      setManual(true);
      return;
    }
    const onSuccess = () => onDone();
    const onError = (cause: unknown) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar"));
    if (initial) update.mutate({ id: initial.id, input: parsed.data }, { onSuccess, onError });
    else add.mutate(parsed.data, { onSuccess, onError });
  }

  return (
    <div className="grid gap-4 rounded-2xl border border-ds-border bg-ds-cream-2/40 p-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[13px] font-semibold text-ds-ink">
          {initial ? t("Editar dirección") : t("Nueva dirección")}
        </p>
        <button
          type="button"
          onClick={onDone}
          aria-label={t("Cancelar")}
          className="rounded-full p-1.5 text-ds-ink-3 transition hover:bg-white hover:text-ds-ink"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <PlaceSearch
        label={t("Escribe tu dirección")}
        onPick={(picked) => {
          set({
            ...(picked.street ? { street: picked.street } : {}),
            ...(picked.city ? { city: picked.city } : {}),
            ...(picked.state ? { state: picked.state } : {}),
            ...(picked.zip ? { zip: picked.zip } : {}),
            ...(picked.placeId ? { placeId: picked.placeId } : {}),
          });
          setManual(true);
        }}
        onUnavailable={() => setManual(true)}
      />

      {!manual && (
        <button
          type="button"
          onClick={() => setManual(true)}
          className="w-fit text-[12.5px] font-medium text-ds-violet-pop hover:underline"
        >
          {t("Escribirla a mano")}
        </button>
      )}

      {manual && (
        <div className="grid gap-3">
          <Input
            label={t("Calle, número y colonia")}
            value={form.street}
            onChange={(event) => set({ street: event.target.value })}
            placeholder={t("Av. Siempre Viva 123, Col. Centro")}
            autoComplete="street-address"
          />
          <div className="grid gap-3 sm:grid-cols-3">
            <Input
              label={t("Ciudad")}
              value={form.city}
              onChange={(event) => set({ city: event.target.value })}
              autoComplete="address-level2"
            />
            <div>
              <Input
                label={t("Estado")}
                value={form.state}
                onChange={(event) => set({ state: event.target.value })}
                list="perfil-estados"
                autoComplete="address-level1"
              />
              <datalist id="perfil-estados">
                {MX_STATES.map((estado) => (
                  <option key={estado} value={estado} />
                ))}
              </datalist>
            </div>
            <Input
              label={t("Código postal")}
              value={form.zip}
              onChange={(event) => set({ zip: event.target.value })}
              inputMode="numeric"
              maxLength={5}
              autoComplete="postal-code"
            />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={t("Etiqueta (opcional)")}
              value={form.label ?? ""}
              onChange={(event) => set({ label: event.target.value })}
              placeholder={t("Casa, Oficina…")}
            />
            <Input
              label={t("Referencia (opcional)")}
              value={form.reference ?? ""}
              onChange={(event) => set({ reference: event.target.value })}
              placeholder={t("Portón negro, entre Reforma y Juárez")}
            />
          </div>
        </div>
      )}

      {error && (
        <Alert variant="error" onDismiss={() => setError(null)}>
          {error}
        </Alert>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onDone}>
          {t("Cancelar")}
        </Button>
        <Button leftIcon={<Check />} loading={saving} onClick={guardar}>
          {t("Guardar dirección")}
        </Button>
      </div>
    </div>
  );
}
